import React, { FC } from 'react';
import {
  IViewResolverLiteProps,
  IViewResolverProviderProps,
  TRendererMap,
  ViewResolverProvider,
} from '@ldp/view-resolver-lite';
import { TViewDefinition } from '../../utils/view-resolver-utils';
import {
  useRendererCallbackMap,
  useRendererMapSSR,
  useSkeletonMap,
  useSSRAnalyticsCallbacks,
} from '../../hooks';
import { SSRThemeProvider } from '@ldp-web/component-library/providers/ssrthemeprovider';

/** Don't forget to add prop types */
export interface IViewResolverSdmInnerSSRProps
  extends Omit<IViewResolverLiteProps, 'rendererMap' | 'skeletonMap' | 'rendererCbMap' | 'isFederated'> {
  children?: React.ReactNode;
  baseFont?: number;
  designTokenPlatform?: string;
  initialData?: TViewDefinition;
  enableSSR?: boolean;
}

/** Inner component which allows the hooks to access common context props */
export const ViewResolverSdmInnerSSR: FC<IViewResolverSdmInnerSSRProps> = ({
  pageId,
  locale = 'en',
  initialData,
  themeTokens,
  baseFont = 12,
  children,
  ...rest
}) => {
  const skeletonMap = useSkeletonMap();
  const rendererMapSSR = useRendererMapSSR();
  const rendererCbMap = useRendererCallbackMap();
  const renderMapData = rendererMapSSR;

  // Trigger analytics callbacks on client-side after hydration
  useSSRAnalyticsCallbacks(initialData, rendererCbMap);

  // Props to be passed to the ViewResolverProvider
  const providerProps: Omit<IViewResolverProviderProps, 'children'> = {
    pageId,
    locale,
    viewDefinition: initialData as any,
    ...rest,
    skeletonMap: skeletonMap,
    rendererMap: renderMapData as unknown as TRendererMap,
    rendererCbMap: rendererCbMap,
    fetchOptions: {},
  };

  return (
    <ViewResolverProvider {...providerProps}>
      <SSRThemeProvider baseTheme={themeTokens} baseFont={baseFont}>
        {children}
      </SSRThemeProvider>
    </ViewResolverProvider>
  );
};

export default ViewResolverSdmInnerSSR;
